import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Fade, Slide, Typography } from '@mui/material';
import { generatePath, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { keyframes } from '@emotion/react';
import routes from '../../../../routes';
import frusLogo from '../../../../assets/svg/new-logo-with-title-color.svg';
import { Colors } from '../../../../assets/theme/Colors';
import useActiveSite from '../../../../hooks/useActiveSite';

const bounce = keyframes`
  from, to { transform: scale(1, 1) translateY(0); }
  25% { transform: scale(0.9, 1.1) translateY(-30px); }
  50% { transform: scale(1.1, 0.9) translateY(0); }
  75% { transform: scale(0.95, 1.05) translateY(-15px); }
`;

const frusLogoSx = {
  width: 200,
  animation: `${bounce} 1000ms 100ms ease`,
};

const ThankYouScreen = ({ showThankYouScreen }) => {
  const history = useHistory();
  const [progress, setProgress] = useState(100);
  const sites = useSelector((state) => state.entities.sites.byId);
  const settings = useSelector((state) => state.entities.settings);
  const site = useActiveSite();

  useEffect(() => {
    if (showThankYouScreen) {
      const timer = setInterval(() => {
        setProgress((prevProgress) => (prevProgress <= 0 ? 0 : prevProgress - 2));
      }, 60);
      return () => {
        clearInterval(timer);
      };
    }
    return () => null;
  }, [showThankYouScreen]);

  useEffect(() => {
    if (progress <= 0) {
      // Wait for animation to finish
      setTimeout(() => {
        history.push(generatePath(routes.index));
      }, 250);
    }
  }, [progress]);

  // Check for custom branding setting
  const currentSite = Object.values(sites).find((s) => s.id === site?.id);
  const settingsList = Object.values(settings.byId);
  const customBrandingSetting = settingsList.find((setting) => setting.name === 'custom_branding');
  const hasCustomBranding =
    currentSite?.settings.find((setting) => setting.id === customBrandingSetting?.id)?.value === '1';

  return (
    <Slide
      direction="up"
      in={showThankYouScreen}
      mountOnEnter
      unmountOnExit
      {...(showThankYouScreen ? { timeout: 300 } : {})}
    >
      <Box
        bgcolor="background.paper"
        width="100%"
        height="100%"
        position="fixed"
        top="0"
        left="0"
        zIndex="modal"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        onClick={() => history.push(generatePath(routes.index))}
      >
        <Box display="flex" flex={1} justifyContent="center" alignItems="flex-end">
          {hasCustomBranding && currentSite?.logo_url ? (
            <Box component="img" alt="logo" sx={frusLogoSx} src={currentSite.logo_url} />
          ) : (
            <Box component="img" alt="frus logo" sx={frusLogoSx} src={frusLogo} />
          )}
        </Box>
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" mt={5}>
          <Typography variant="h3" style={{lineHeight: 1.5}}>Thank you!</Typography>
          <Typography variant="h4">For being the rescue.</Typography>
          <Fade
            in={showThankYouScreen}
            style={{ transitionDelay: showThankYouScreen ? '1000ms' : '0ms', marginTop: '1rem' }}
            {...(showThankYouScreen ? { timeout: 700 } : {})}
          >
            <Typography variant="body2">(Click anywhere to return)</Typography>
          </Fade>
        </Box>
        <Box flex={1} display="flex" justifyContent="center" alignItems="center">
          <CircularProgress
            variant="determinate"
            value={progress}
            style={{ transform: 'scaleX(-1) rotate(-90deg', color: Colors.appBar.main }}
            size={60}
            thickness={5}
          />
        </Box>
      </Box>
    </Slide>
  );
};

export default ThankYouScreen;
